import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useSyncExternalStore,
  type ReactElement,
} from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { useNavigate } from "@tanstack/react-router";
import { apiDocsRoute } from "../../routes/api-docs";
import { useApiDocsStore } from "./store";
import { Button } from "../../ui/Button/Button";
import { Alert } from "../../ui/Alert/Alert";
import { Icon } from "../../ui/Icon/Icon";
import { useShellChrome } from "../../shell/shellChrome";
import styles from "./ApiDocs.module.css";

const RedocDoc = lazy(() =>
  import("./RedocDoc").then((module) => ({ default: module.RedocDoc })),
);

// Fullscreen focus is a desktop affordance; on mobile/tablet Redoc adapts on
// its own, so the controls are hidden below this width.
const DESKTOP_QUERY = "(min-width: 1024px)";

function useCanFullscreen(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const media = window.matchMedia(DESKTOP_QUERY);
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    },
    () => window.matchMedia(DESKTOP_QUERY).matches,
    () => true,
  );
}

// Mirrors Redoc's three columns so the swap to real content barely shifts:
// sidebar (search + grouped nav, some rows with a method badge), the middle
// article, and the dark sample panel.
const MENU_GROUPS: { rows: { w: number; badge: boolean }[] }[] = [
  {
    rows: [
      { w: 58, badge: false },
      { w: 72, badge: false },
      { w: 64, badge: false },
    ],
  },
  {
    rows: [
      { w: 60, badge: true },
      { w: 78, badge: true },
      { w: 52, badge: true },
      { w: 70, badge: true },
    ],
  },
  {
    rows: [
      { w: 66, badge: true },
      { w: 56, badge: true },
    ],
  },
];
const ARTICLE_SECTIONS = [
  [98, 100, 92, 96, 70],
  [94, 100, 88, 62],
];
const CODE_LINES = [46, 72, 60, 84, 38, 66, 52, 78, 44, 70];

function DocsSkeleton(): ReactElement {
  return (
    <div className={styles.skeleton} aria-hidden="true">
      <SkeletonTheme
        baseColor="var(--color-neutral-01)"
        highlightColor="var(--color-neutral-02)"
        borderRadius={8}
      >
        <div className={styles.skMenu}>
          <div className={styles.skSearch}>
            <Skeleton circle width={15} height={15} />
            <Skeleton containerClassName={styles.skSearchText} width="55%" height={12} />
          </div>
          {MENU_GROUPS.map((group, gi) => (
            <div key={gi} className={styles.skGroup}>
              <Skeleton width={90} height={9} />
              {group.rows.map((row, ri) => (
                <div key={ri} className={styles.skRow}>
                  <Skeleton
                    containerClassName={styles.skGrow}
                    width={`${row.w}%`}
                    height={14}
                  />
                  {row.badge ? <Skeleton width={32} height={11} /> : null}
                </div>
              ))}
            </div>
          ))}
        </div>
      </SkeletonTheme>

      <SkeletonTheme
        baseColor="var(--color-neutral-01)"
        highlightColor="var(--color-neutral-02)"
        borderRadius={6}
      >
        <div className={styles.skArticle}>
          <div className={styles.skTitle}>
            <Skeleton width="52%" height={30} />
            <Skeleton width="78%" height={14} />
          </div>
          {ARTICLE_SECTIONS.map((lines, si) => (
            <div key={si} className={styles.skPara}>
              <Skeleton width="34%" height={20} />
              {lines.map((w, i) => (
                <Skeleton key={i} width={`${w}%`} height={14} />
              ))}
            </div>
          ))}
        </div>
      </SkeletonTheme>

      <SkeletonTheme baseColor="#2a2350" highlightColor="#3b3170" borderRadius={5}>
        <div className={styles.skPanel}>
          <Skeleton width="42%" height={11} />
          <div className={styles.skCode}>
            {CODE_LINES.map((w, i) => (
              <Skeleton key={i} width={`${w}%`} height={9} />
            ))}
          </div>
        </div>
      </SkeletonTheme>
    </div>
  );
}

export function ApiDocsView(): ReactElement {
  const spec = useApiDocsStore((state) => state.spec);
  const specStatus = useApiDocsStore((state) => state.status);
  const loadSpec = useApiDocsStore((state) => state.load);
  const specPending = specStatus === "idle" || specStatus === "loading";
  const { setHidden } = useShellChrome();

  useEffect(() => {
    void loadSpec();
  }, [loadSpec]);
  const canFullscreen = useCanFullscreen();
  const navigate = useNavigate();
  const { fullscreen } = apiDocsRoute.useSearch();

  // Focus mode lives in the URL (?fullscreen), so a shared/saved link restores
  // it. On viewports too small for it the flag is kept but stays inert, so the
  // same link reopens full screen when viewed on a desktop.
  const focus = Boolean(fullscreen) && canFullscreen;
  const setFocus = useCallback(
    (on: boolean) => {
      void navigate({
        to: "/api-documentation",
        search: on ? { fullscreen: true } : {},
        hash: window.location.hash.replace(/^#/, "") || undefined,
        replace: true,
      });
    },
    [navigate],
  );

  useEffect(() => {
    setHidden(focus);
    if (!focus) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFocus(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [focus, setHidden, setFocus]);

  useEffect(() => () => setHidden(false), [setHidden]);

  return (
    <div className={`${styles.docs} ${focus ? styles.focusMode : ""}`}>
      {focus ? (
        <div className={styles.exitBar} data-sticky-top>
          <span className={styles.exitTitle}>API documentation</span>
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<Icon name="minimize" size={16} />}
            onClick={() => setFocus(false)}
          >
            Exit full width
          </Button>
        </div>
      ) : (
        <header className={styles.header}>
          <div className={styles.headingCol}>
            <h1 className={styles.title}>API documentation</h1>
            <p className={styles.subtitle}>
              Everything you need to build on the Property Finder API —
              endpoints, request and response schemas, and ready-to-run cURL
              examples.
            </p>
          </div>
          {canFullscreen ? (
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.fullBtn}
                onClick={() => setFocus(true)}
              >
                <Icon name="fullscreen" size={16} />
                Full width
              </button>
            </div>
          ) : null}
        </header>
      )}

      <div className={styles.frame}>
        {specPending ? (
          <DocsSkeleton />
        ) : specStatus === "error" || !spec ? (
          <div className={styles.errorState}>
            <Alert
              tone="danger"
              title="Couldn't load the API reference"
              action={
                <Button variant="secondary" onClick={() => void loadSpec()}>
                  Try again
                </Button>
              }
            >
              Your session may have expired. Try again, or sign in first.
            </Alert>
          </div>
        ) : (
          <Suspense fallback={<DocsSkeleton />}>
            <RedocDoc spec={spec} />
          </Suspense>
        )}
      </div>

      {/* Desktop-only floating control to enter fullscreen (the header button
          scrolls away). In focus mode the sticky exit bar is the control. */}
      {canFullscreen && !focus ? (
        <button
          type="button"
          className={styles.fab}
          onClick={() => setFocus(true)}
          aria-label="Full width"
          title="Full width"
        >
          <Icon name="fullscreen" size={20} />
        </button>
      ) : null}
    </div>
  );
}
